// src/pages/preventivas/CalendarioPreventivasPage.jsx
import React, { useEffect, useMemo, useState } from "react";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";

import { db } from "../../config/firebase";
import { collection, getDocs, query, where } from "firebase/firestore";

import { formatData, timestampToDate } from "../../utils/dateFormat";
import { montarNomeExibicaoPreventiva } from "../../utils/preventivaNome";

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

function CalendarioPreventivasPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [preventivas, setPreventivas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [textoFiltro, setTextoFiltro] = useState("");

  const [abaAtiva, setAbaAtiva] = useState("calendario");
  const [mesAtual, setMesAtual] = useState(new Date().getMonth());
  const [anoAtual, setAnoAtual] = useState(new Date().getFullYear());
  const [diaSelecionado, setDiaSelecionado] = useState(null);

  useEffect(() => {
    async function carregarPreventivas() {
      if (!usuarioLogado?.grupoId) return;

      setCarregando(true);
      setErro("");

      try {
        const ref = collection(db, "preventivas");
        const q = query(ref, where("grupoId", "==", usuarioLogado.grupoId));
        const snap = await getDocs(q);

        const lista = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter((p) => p?.ativo !== false)
        .map((p) => {
            const d = timestampToDate(p.proximaData);
            return {
              ...p,
              proximaDataReal: d,
            };
          })
          .filter((p) => !!p.proximaDataReal)
          .sort(
            (a, b) =>
              (a.proximaDataReal?.getTime() || 0) -
              (b.proximaDataReal?.getTime() || 0)
          );

        setPreventivas(lista);
      } catch (e) {
        console.error("Erro ao carregar calendário de preventivas:", e);
        setErro("Erro ao carregar calendário de preventivas. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarPreventivas();
  }, [usuarioLogado]);

  const preventivasFiltradas = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return preventivas;

    return preventivas.filter((p) => {
      const campos = [
        p.codigo,
        p.nome,
        montarNomeExibicaoPreventiva(p),
        p.empresaNome,
        p.maquinaNome,
        p.periodicidade,
      ];
      return campos.some((campo) =>
        String(campo || "").toLowerCase().includes(texto)
      );
    });
  }, [preventivas, textoFiltro]);

  const preventivasPorDia = useMemo(() => {
    const mapa = {};
    preventivas.forEach((p) => {
      if (p.proximaDataReal) {
        const dataKey = `${p.proximaDataReal.getFullYear()}-${p.proximaDataReal.getMonth()}-${p.proximaDataReal.getDate()}`;
        if (!mapa[dataKey]) {
          mapa[dataKey] = [];
        }
        mapa[dataKey].push(p);
      }
    });
    return mapa;
  }, [preventivas]);

  function getDiasDoMes(ano, mes) {
    const primeiroDia = new Date(ano, mes, 1);
    const ultimoDia = new Date(ano, mes + 1, 0);
    const diasNoMes = ultimoDia.getDate();
    const diaSemanaInicio = primeiroDia.getDay();

    const dias = [];

    for (let i = 0; i < diaSemanaInicio; i++) {
      dias.push(null);
    }

    for (let dia = 1; dia <= diasNoMes; dia++) {
      dias.push(dia);
    }

    return dias;
  }

  function navegarMes(direcao) {
    let novoMes = mesAtual + direcao;
    let novoAno = anoAtual;

    if (novoMes < 0) {
      novoMes = 11;
      novoAno -= 1;
    } else if (novoMes > 11) {
      novoMes = 0;
      novoAno += 1;
    }

    setMesAtual(novoMes);
    setAnoAtual(novoAno);
    setDiaSelecionado(null);
  }

  function irParaHoje() {
    const hoje = new Date();
    setMesAtual(hoje.getMonth());
    setAnoAtual(hoje.getFullYear());
    setDiaSelecionado(hoje.getDate());
  }

  function getPreventivasDoDia(dia) {
    if (!dia) return [];
    const dataKey = `${anoAtual}-${mesAtual}-${dia}`;
    return preventivasPorDia[dataKey] || [];
  }

  const diasDoMes = getDiasDoMes(anoAtual, mesAtual);
  const hoje = new Date();
  const ehHoje = (dia) =>
    dia === hoje.getDate() &&
    mesAtual === hoje.getMonth() &&
    anoAtual === hoje.getFullYear();

  const preventivasDoDiaSelecionado = diaSelecionado
    ? getPreventivasDoDia(diaSelecionado)
    : [];

  const calendarioView = (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={() => navegarMes(-1)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #334155",
              
              color: "inherit",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ◀
          </button>
          <span style={{ fontSize: 18, fontWeight: 600, minWidth: 180, textAlign: "center" }}>
            {MESES[mesAtual]} {anoAtual}
          </span>
          <button
            type="button"
            onClick={() => navegarMes(1)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #334155",
              
              color: "inherit",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ▶
          </button>
        </div>
        <button
          type="button"
          onClick={irParaHoje}
          className="pc-btn-secundario"
          style={{ padding: "8px 16px" }}
        >
          Hoje
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
          background: "rgba(255,255,255,0.03)",
          borderRadius: 12,
          padding: 8,
        }}
      >
        {DIAS_SEMANA.map((dia) => (
          <div
            key={dia}
            style={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: 13,
              padding: "8px 0",
              opacity: 0.7,
            }}
          >
            {dia}
          </div>
        ))}

        {diasDoMes.map((dia, idx) => {
          const prevDoDia = getPreventivasDoDia(dia);
          const temPreventiva = prevDoDia.length > 0;
          const selecionado = dia === diaSelecionado;

          return (
            <div
              key={idx}
              onClick={() => dia && setDiaSelecionado(dia)}
              style={{
                minHeight: isMobile ? 50 : 70,
                padding: 6,
                borderRadius: 8,
                background: selecionado
                  ? "rgba(59, 130, 246, 0.3)"
                  : ehHoje(dia)
                  ? "rgba(34, 197, 94, 0.15)"
                  : dia
                  ? "rgba(255,255,255,0.03)"
                  : "transparent",
                border: selecionado
                  ? "2px solid #3b82f6"
                  : ehHoje(dia)
                  ? "2px solid #22c55e"
                  : "1px solid transparent",
                cursor: dia ? "pointer" : "default",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                transition: "all 0.15s ease",
              }}
            >
              {dia && (
                <>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: ehHoje(dia) ? 700 : 500,
                      color: ehHoje(dia) ? "#22c55e" : "inherit",
                    }}
                  >
                    {dia}
                  </span>
                  {temPreventiva && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: 2,
                      }}
                    >
                      {prevDoDia.slice(0, 3).map((p, i) => (
                        <div
                          key={i}
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            background: "#3b82f6",
                          }}
                          title={montarNomeExibicaoPreventiva(p) || p.codigo}
                        />
                      ))}
                      {prevDoDia.length > 3 && (
                        <span style={{ fontSize: 10, opacity: 0.7 }}>
                          +{prevDoDia.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {diaSelecionado && (
        <div
          style={{
            background: "rgba(255,255,255,0.05)",
            borderRadius: 12,
            padding: 16,
            marginTop: 8,
          }}
        >
          <h4 style={{ margin: 0, marginBottom: 12 }}>
            Preventivas em {diaSelecionado}/{mesAtual + 1}/{anoAtual}
          </h4>
          {preventivasDoDiaSelecionado.length === 0 ? (
            <p style={{ opacity: 0.7, margin: 0 }}>
              Nenhuma preventiva agendada para este dia.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {preventivasDoDiaSelecionado.map((p) => (
                <div
                  key={p.id}
                  style={{
                    background: "rgba(59, 130, 246, 0.1)",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{montarNomeExibicaoPreventiva(p) || "Sem nome"}</div>
                  <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                    <span style={{ marginRight: 12 }}>📋 {p.codigo || "-"}</span>
                    <span style={{ marginRight: 12 }}>🏢 {p.empresaNome || "-"}</span>
                    <span>🔧 {p.maquinaNome || "-"}</span>
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                    Periodicidade: {p.periodicidade || "-"} | Itens: {p.itens?.length || 0}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const listaView = (
    <>
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          value={textoFiltro}
          onChange={(e) => setTextoFiltro(e.target.value)}
          placeholder="Filtrar por código, nome, empresa, máquina..."
          style={{
            width: "100%",
            maxWidth: 420,
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #334155",
            
            color: "inherit",
          }}
        />
      </div>

      <div className="pc-table-wrapper">
        {erro && (
          <div
            style={{
              background: "#7f1d1d",
              color: "#fecaca",
              padding: 8,
              borderRadius: 8,
              marginBottom: 12,
              fontSize: 14,
            }}
          >
            {erro}
          </div>
        )}

        {carregando ? (
          <p style={{ padding: 10 }}>Carregando calendário de preventivas...</p>
        ) : preventivasFiltradas.length === 0 ? (
          <p style={{ padding: 10 }}>Nenhuma preventiva com próxima data definida.</p>
        ) : (
          <table className="pc-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Código</th>
                <th>Nome</th>
                <th>Empresa</th>
                <th>Máquina</th>
                <th>Periodicidade</th>
              </tr>
            </thead>
            <tbody>
              {preventivasFiltradas.map((p) => (
                <tr key={p.id}>
                  <td>{formatData(p.proximaDataReal)}</td>
                  <td>{p.codigo || "-"}</td>
                  <td>{montarNomeExibicaoPreventiva(p) || "-"}</td>
                  <td>{p.empresaNome || "-"}</td>
                  <td>{p.maquinaNome || "-"}</td>
                  <td>{p.periodicidade || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );

  const abas = (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginBottom: 16,
        borderBottom: "1px solid #334155",
        paddingBottom: 8,
      }}
    >
      <button
        type="button"
        onClick={() => setAbaAtiva("calendario")}
        style={{
          padding: "8px 20px",
          borderRadius: 8,
          border: "none",
          background: abaAtiva === "calendario" ? "#3b82f6" : "transparent",
          color: abaAtiva === "calendario" ? "#fff" : "inherit",
          cursor: "pointer",
          fontWeight: abaAtiva === "calendario" ? 600 : 400,
        }}
      >
        📅 Calendário
      </button>
      <button
        type="button"
        onClick={() => setAbaAtiva("lista")}
        style={{
          padding: "8px 20px",
          borderRadius: 8,
          border: "none",
          background: abaAtiva === "lista" ? "#3b82f6" : "transparent",
          color: abaAtiva === "lista" ? "#fff" : "inherit",
          cursor: "pointer",
          fontWeight: abaAtiva === "lista" ? 600 : 400,
        }}
      >
        📋 Lista
      </button>
    </div>
  );

  const conteudo = (
    <>
      {abas}
      {carregando ? (
        <p style={{ padding: 10 }}>Carregando...</p>
      ) : abaAtiva === "calendario" ? (
        calendarioView
      ) : (
        listaView
      )}
    </>
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Calendário de Preventivas"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      >
        <div style={{ padding: 12 }}>{conteudo}</div>
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Calendário de Preventivas"
      subtitulo=""
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Calendário de preventivas</h3>
            <p>
              Visualize as próximas execuções de preventivas no calendário ou em lista.
            </p>
          </div>
        </header>

        {conteudo}
      </section>
    </LayoutDesktop>
  );
}

export default CalendarioPreventivasPage;
